import { axiosDefaultInstance } from '@/http/default'
import type { Bill } from '@/model/Bill'
import type Company from '@/model/Company'
import type { FeatureFlagsCompany } from '@/model/FeatureFlagsCompany'
import type { CompanyBlockExplanation } from '@/model/Company'
import type Customer from '@/model/Customer'
import type { CustomerBulkCreateRs } from '@/model/Customer'
import { CustomerType } from '@/model/Customer'
import { PlaceType } from '@/model/Place'
import type { VehicleBulkCreateRs } from '@/model/Vehicle'
import type { PlaceBulkCreateRs } from '@/model/Place'
import type { PayloadBulkCreateRs } from '@/model/Payload'
import type { DeliveryNote, DeliveryNoteImages, OperatorRole } from '@/model/DeliveryNote'
import type Employee from '@/model/Employee'
import type { EmployeeBilling, EmployeeBulkCreateRs } from '@/model/Employee'
import { Role } from '@/model/Employee'
import type LoadOrder from '@/model/LoadOrder'
import type {
  PassengerTransportDocument,
  PassengerTransportDocumentType
} from '@/model/PassengerTransportDocument'
import type Page from '@/model/Page'
import type Driver from '@/model/Driver'
import type Payload from '@/model/Payload'
import type Place from '@/model/Place'
import type Price from '@/model/Price'
import type Vehicle from '@/model/Vehicle'
import type { AxiosInstance } from 'axios'
import type AIRequest from '@/model/AIRequest'
import type { AIRequestListItem } from '@/model/AIRequest'
import type { CompanyRegistrationRequest } from './CompanyService'
import handleError from './util/ErrorHandler'
import { ServiceError } from './util/ServiceError'
import type { AdminBillingSummaryData } from '@/utils/adminBillingSummaryUtils'
import type {
  AdminBillingPeriodMetricsData,
  AdminBillingTrendsData,
  AdminBillingTrialsData
} from '@/utils/adminBillingMetricsUtils'
import type { AdminDashboardData, ChartGranularity } from '@/utils/adminDashboardUtils'
import type { BillingSummary, BillingOrigin, KargoBill } from '@/model/Billing'
import { normalizeBillingSummary, normalizeKargoBill } from '@/model/Billing'

export type { CompanyRegistrationRequest }

export type GetCompaniesOptions = {
  blocked?: boolean
  name?: string
  vatNumber?: string
  originCode?: string[]
}

export interface AdminDeliveryNoteFilters {
  sort?: string
  direction?: string
  startDate?: string
  endDate?: string
  numbers?: string[]
  operatorRole?: OperatorRole[]
  customerId?: string[]
  customerName?: string
  customerVatNumber?: string
  employeeId?: string[]
  driverId?: string[]
  driverNif?: string[]
  driverName?: string
  driverSurname?: string
  vehicleId?: string[]
  originId?: string[]
  destinationId?: string[]
  originName?: string
  destinationName?: string
  payload?: string
  payloadId?: string[]
  billed?: boolean
  uncompleted?: boolean
  manualValidationRequired?: boolean
}

const FILTER_OPTIONS_PAGE_SIZE = 200

export interface AdminBillFilters {
  sort?: string
  direction?: string
  startDate?: string
  endDate?: string
  customerId?: string[]
}

export interface AdminPlaceFilters {
  sort?: string
  direction?: string
  name?: string
  types?: string[]
}

export interface AdminAiRequestFilters {
  sort?: string
  direction?: string
  type?: string
  startDate?: string
  endDate?: string
  hasError?: boolean
}

export interface AdminCustomerFilters {
  sort?: string
  direction?: string
  name?: string
  email?: string
  vatNumber?: string
  phone?: string
}

export interface AdminEmployeeFilters {
  sort?: string
  direction?: string
  name?: string
  surname?: string
  email?: string
  nif?: string
  phoneNumber?: string
  role?: string
  showInactive?: boolean
}

export interface AdminDriverFilters {
  sort?: string
  direction?: string
  name?: string
  surname?: string
  email?: string
  nif?: string
  phoneNumber?: string
}

export interface AdminPriceFilters {
  sort?: string
  direction?: string
  payload?: string
}

export interface AdminLoadOrderFilters {
  sort?: string
  direction?: string
  startDate?: string
  endDate?: string
  requestStartDate?: string
  requestEndDate?: string
  numbers?: string[]
  customerId?: string[]
  employeeId?: string[]
  driverId?: string[]
  vehicleId?: string[]
  originId?: string[]
  destinationId?: string[]
  originName?: string
  destinationName?: string
  customerName?: string
  customerVatNumber?: string
  driverName?: string
  driverSurname?: string
  payload?: string
  payloadId?: string[]
  withoutDeliveryNote?: boolean
  withDeliveryNote?: boolean
}

export interface AdminPassengerTransportDocumentFilters {
  sort?: string
  direction?: string
  startDate?: string
  endDate?: string
  numbers?: string[]
  customerId?: string[]
  employeeId?: string[]
  driverId?: string[]
  vehicleId?: string[]
  originId?: string[]
  destinationId?: string[]
  originName?: string
  destinationName?: string
  customerName?: string
  customerVatNumber?: string
  billed?: boolean
  manualValidationRequired?: boolean
  type?: PassengerTransportDocumentType[]
}

class AdminService {
  private axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axiosDefaultInstance
  }

  async getCompanies(
    page: number = 0,
    size: number = 20,
    sort?: string,
    options?: GetCompaniesOptions
  ): Promise<Page<Company> | ServiceError> {
    try {
      const params: Record<string, string | number | boolean | string[]> = {
        pageNumber: page,
        pageSize: size
      }

      if (sort) {
        params.sort = sort
      }

      if (options?.blocked !== undefined) {
        params.blocked = options.blocked
      }

      if (options?.name) {
        params.name = options.name
      }

      if (options?.vatNumber) {
        params.vatNumber = options.vatNumber
      }

      if (options?.originCode?.length) {
        params.originCode = options.originCode
      }

      const networkResponse = await this.axiosInstance.get<Page<Company>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompaniesCsv(
    options: GetCompaniesOptions & { sort?: string; direction?: string } = {}
  ): Promise<Blob | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') ?? 'es'
      const params: Record<string, string | number | boolean | string[]> = { locale }

      if (options.sort) {
        params.sort = options.sort
      }

      if (options.direction) {
        params.direction = options.direction
      }

      if (options.blocked !== undefined) {
        params.blocked = options.blocked
      }

      if (options.name) {
        params.name = options.name
      }

      if (options.vatNumber) {
        params.vatNumber = options.vatNumber
      }

      if (options.originCode?.length) {
        params.originCode = options.originCode
      }

      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/csv`,
        {
          params,
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyById(companyId: string): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateCompanyBillingCompany(
    companyId: string,
    billingCompanyId: string | null
  ): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/billing-company`,
        { billingCompanyId }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateManualTrialSubscriptionEnd(
    companyId: string,
    manualTrialSubscriptionEnd: string | null
  ): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/manual-trial-subscription-end`,
        { manualTrialSubscriptionEnd }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateCompanyBlock(
    companyId: string,
    payload: {
      blocked: boolean
      date?: string | null
      explanation?: CompanyBlockExplanation | null
    }
  ): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/block`,
        payload
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyFeatureFlags(companyId: string): Promise<FeatureFlagsCompany | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<FeatureFlagsCompany>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/feature-flags`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateCompanyStripeBillingEnabled(
    companyId: string,
    stripeBillingEnabled: boolean
  ): Promise<FeatureFlagsCompany | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<FeatureFlagsCompany>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/feature-flags/stripe-billing`,
        { stripeBillingEnabled }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateOriginCode(
    companyId: string,
    originCode: string | null
  ): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/origin-code`,
        { originCode }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyBillingSummary(
    companyId: string,
    locale?: string
  ): Promise<BillingSummary | ServiceError> {
    try {
      const resolvedLocale = locale ?? localStorage.getItem('appLanguage') ?? 'es'
      const networkResponse = await this.axiosInstance.get<BillingSummary>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/billing/summary`,
        { params: { locale: resolvedLocale } }
      )
      return normalizeBillingSummary(networkResponse.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyBillingBills(companyId: string): Promise<KargoBill[] | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<KargoBill[]>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/billing/bills`
      )
      return networkResponse.data.map(normalizeKargoBill)
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompanyBillingBillPdf(
    companyId: string,
    billId: string,
    locale?: string
  ): Promise<Blob | ServiceError> {
    try {
      const resolvedLocale = locale ?? localStorage.getItem('appLanguage') ?? 'es'
      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/billing/bills/${billId}/pdf`,
        {
          params: { locale: resolvedLocale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createCompany(
    registrationData: CompanyRegistrationRequest
  ): Promise<Company | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.post<Company>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company`,
        registrationData
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyDeliveryNotes(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminDeliveryNoteFilters = {}
  ): Promise<Page<DeliveryNote> | ServiceError> {
    try {
      const params: Record<string, string | number | string[]> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.startDate) {
        params.startDate = filters.startDate
      }

      if (filters.endDate) {
        params.endDate = filters.endDate
      }

      if (filters.numbers?.length) {
        params.numbers = filters.numbers
      }

      if (filters.operatorRole?.length) {
        params.operatorRole = filters.operatorRole
      }

      if (filters.customerId?.length) {
        params.customerId = filters.customerId
      }

      if (filters.customerName) {
        params.customerName = filters.customerName
      }

      if (filters.customerVatNumber) {
        params.customerVatNumber = filters.customerVatNumber
      }

      if (filters.employeeId?.length) {
        params.employeeId = filters.employeeId
      }

      if (filters.driverId?.length) {
        params.driverId = filters.driverId
      }

      if (filters.driverNif?.length) {
        params.driverNif = filters.driverNif
      }

      if (filters.driverName) {
        params.driverName = filters.driverName
      }

      if (filters.driverSurname) {
        params.driverSurname = filters.driverSurname
      }

      if (filters.vehicleId?.length) {
        params.vehicleId = filters.vehicleId
      }

      if (filters.originId?.length) {
        params.originId = filters.originId
      }

      if (filters.destinationId?.length) {
        params.destinationId = filters.destinationId
      }

      if (filters.originName) {
        params.originName = filters.originName
      }

      if (filters.destinationName) {
        params.destinationName = filters.destinationName
      }

      if (filters.payload) {
        params.payload = filters.payload
      }

      if (filters.payloadId?.length) {
        params.payloadId = filters.payloadId
      }

      if (filters.billed === true || filters.billed === false) {
        params.billed = String(filters.billed)
      }

      if (filters.uncompleted) {
        params.uncompleted = String(filters.uncompleted)
      }

      if (filters.manualValidationRequired) {
        params.manualValidationRequired = String(filters.manualValidationRequired)
      }

      const networkResponse = await this.axiosInstance.get<Page<DeliveryNote>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/delivery-note`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyDeliveryNoteById(
    companyId: string,
    deliveryNoteId: string
  ): Promise<DeliveryNote | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/delivery-note/${deliveryNoteId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyDeliveryNoteImages(
    companyId: string,
    deliveryNoteId: string
  ): Promise<DeliveryNoteImages | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNoteImages>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/delivery-note/${deliveryNoteId}/images`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyLoadOrders(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminLoadOrderFilters = {}
  ): Promise<Page<LoadOrder> | ServiceError> {
    try {
      const params: Record<string, string | number | string[] | boolean> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.startDate) {
        params.startDate = filters.startDate
      }

      if (filters.endDate) {
        params.endDate = filters.endDate
      }

      if (filters.requestStartDate) {
        params.requestStartDate = filters.requestStartDate
      }

      if (filters.requestEndDate) {
        params.requestEndDate = filters.requestEndDate
      }

      if (filters.numbers?.length) {
        params.numbers = filters.numbers
      }

      if (filters.customerId?.length) {
        params.customerId = filters.customerId
      }

      if (filters.employeeId?.length) {
        params.employeeId = filters.employeeId
      }

      if (filters.driverId?.length) {
        params.driverId = filters.driverId
      }

      if (filters.vehicleId?.length) {
        params.vehicleId = filters.vehicleId
      }

      if (filters.originId?.length) {
        params.originId = filters.originId
      }

      if (filters.destinationId?.length) {
        params.destinationId = filters.destinationId
      }

      if (filters.originName) {
        params.originName = filters.originName
      }

      if (filters.destinationName) {
        params.destinationName = filters.destinationName
      }

      if (filters.customerName) {
        params.customerName = filters.customerName
      }

      if (filters.customerVatNumber) {
        params.customerVatNumber = filters.customerVatNumber
      }

      if (filters.driverName) {
        params.driverName = filters.driverName
      }

      if (filters.driverSurname) {
        params.driverSurname = filters.driverSurname
      }

      if (filters.payload) {
        params.payload = filters.payload
      }

      if (filters.payloadId?.length) {
        params.payloadId = filters.payloadId
      }

      if (filters.withoutDeliveryNote) {
        params.withoutDeliveryNote = String(filters.withoutDeliveryNote)
      }

      if (filters.withDeliveryNote) {
        params.withDeliveryNote = String(filters.withDeliveryNote)
      }

      const networkResponse = await this.axiosInstance.get<Page<LoadOrder>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/load-order`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyLoadOrderById(
    companyId: string,
    loadOrderId: string
  ): Promise<LoadOrder | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<LoadOrder>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/load-order/${loadOrderId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPassengerTransportDocuments(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminPassengerTransportDocumentFilters = {}
  ): Promise<Page<PassengerTransportDocument> | ServiceError> {
    try {
      const params: Record<string, string | number | string[] | boolean> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.startDate) {
        params.startDate = filters.startDate
      }

      if (filters.endDate) {
        params.endDate = filters.endDate
      }

      if (filters.numbers?.length) {
        params.numbers = filters.numbers
      }

      if (filters.customerId?.length) {
        params.customerId = filters.customerId
      }

      if (filters.employeeId?.length) {
        params.employeeId = filters.employeeId
      }

      if (filters.driverId?.length) {
        params.driverId = filters.driverId
      }

      if (filters.vehicleId?.length) {
        params.vehicleId = filters.vehicleId
      }

      if (filters.originId?.length) {
        params.originId = filters.originId
      }

      if (filters.destinationId?.length) {
        params.destinationId = filters.destinationId
      }

      if (filters.originName) {
        params.originName = filters.originName
      }

      if (filters.destinationName) {
        params.destinationName = filters.destinationName
      }

      if (filters.customerName) {
        params.customerName = filters.customerName
      }

      if (filters.customerVatNumber) {
        params.customerVatNumber = filters.customerVatNumber
      }

      if (filters.billed === true || filters.billed === false) {
        params.billed = String(filters.billed)
      }

      if (filters.manualValidationRequired) {
        params.manualValidationRequired = String(filters.manualValidationRequired)
      }

      if (filters.type?.length) {
        params.type = filters.type
      }

      const networkResponse = await this.axiosInstance.get<Page<PassengerTransportDocument>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/passenger-transport-document`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPassengerTransportDocumentById(
    companyId: string,
    passengerTransportDocumentId: string
  ): Promise<PassengerTransportDocument | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<PassengerTransportDocument>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/passenger-transport-document/${passengerTransportDocumentId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPassengerTransportDocumentImages(
    companyId: string,
    passengerTransportDocumentId: string
  ): Promise<DeliveryNoteImages | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNoteImages>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/passenger-transport-document/${passengerTransportDocumentId}/images`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompanyPassengerTransportDocument(
    companyId: string,
    passengerTransportDocumentId: string
  ): Promise<Blob | ServiceError> {
    const locale = localStorage.getItem('appLanguage') || 'es'

    try {
      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/passenger-transport-document/${passengerTransportDocumentId}/pdf`,
        {
          params: { locale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompanyDeliveryNote(
    companyId: string,
    deliveryNoteId: string
  ): Promise<Blob | ServiceError> {
    const locale = localStorage.getItem('appLanguage') || 'es'

    try {
      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/delivery-note/${deliveryNoteId}/pdf`,
        {
          params: { locale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompanyDeliveryNoteCmr(
    companyId: string,
    deliveryNoteId: string
  ): Promise<Blob | ServiceError> {
    const locale = localStorage.getItem('appLanguage') || 'es'

    try {
      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/delivery-note/${deliveryNoteId}/cmr/pdf`,
        {
          params: { locale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyEmployeeById(
    companyId: string,
    employeeId: string
  ): Promise<Employee | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/employee/${employeeId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getEmployeeBilling(employeeId: string): Promise<EmployeeBilling | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<EmployeeBilling>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/employee/${employeeId}/billing`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateEmployeeBillingCompany(
    employeeId: string,
    billingCompanyId: string
  ): Promise<EmployeeBilling | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<EmployeeBilling>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/employee/${employeeId}/billing-company`,
        { billingCompanyId }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateEmployeeEmail(
    companyId: string,
    employeeId: string,
    email: string
  ): Promise<Employee | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/employee/${employeeId}/email`,
        { email }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateEmployeePassword(
    companyId: string,
    employeeId: string,
    password: string
  ): Promise<Employee | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.put<Employee>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/employee/${employeeId}/password`,
        { password }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyEmployees(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminEmployeeFilters = {}
  ): Promise<Page<Employee> | ServiceError> {
    try {
      const params: Record<string, string | number | boolean> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.surname) {
        params.surname = filters.surname
      }

      if (filters.email) {
        params.email = filters.email
      }

      if (filters.nif) {
        params.nif = filters.nif
      }

      if (filters.phoneNumber) {
        params.phoneNumber = filters.phoneNumber
      }

      if (filters.role) {
        params.role = filters.role
      }

      if (filters.showInactive) {
        params.showInactive = filters.showInactive
      }

      const networkResponse = await this.axiosInstance.get<Page<Employee>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/employee`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getAllEmployees(
    page: number = 0,
    size: number = 20,
    filters: AdminEmployeeFilters = {}
  ): Promise<Page<Employee> | ServiceError> {
    try {
      const params: Record<string, string | number | boolean> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.surname) {
        params.surname = filters.surname
      }

      if (filters.email) {
        params.email = filters.email
      }

      if (filters.nif) {
        params.nif = filters.nif
      }

      if (filters.phoneNumber) {
        params.phoneNumber = filters.phoneNumber
      }

      if (filters.role) {
        params.role = filters.role
      }

      if (filters.showInactive) {
        params.showInactive = filters.showInactive
      }

      const networkResponse = await this.axiosInstance.get<Page<Employee>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/employee`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllEmployeesCsv(
    filters: AdminEmployeeFilters = {}
  ): Promise<Blob | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') ?? 'es'
      const params: Record<string, string | number | boolean> = { locale }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.surname) {
        params.surname = filters.surname
      }

      if (filters.email) {
        params.email = filters.email
      }

      if (filters.nif) {
        params.nif = filters.nif
      }

      if (filters.phoneNumber) {
        params.phoneNumber = filters.phoneNumber
      }

      if (filters.role) {
        params.role = filters.role
      }

      if (filters.showInactive) {
        params.showInactive = filters.showInactive
      }

      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/employee/csv`,
        {
          params,
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyBills(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminBillFilters = {}
  ): Promise<Page<Bill> | ServiceError> {
    try {
      const params: Record<string, string | number | string[]> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.startDate) {
        params.startDate = filters.startDate
      }

      if (filters.endDate) {
        params.endDate = filters.endDate
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.customerId?.length) {
        params.customerId = filters.customerId
      }

      const networkResponse = await this.axiosInstance.get<Page<Bill>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/bill`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyBillById(companyId: string, billId: string): Promise<Bill | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Bill>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/bill/${billId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCompanyBill(
    companyId: string,
    billId: string,
    addDeliveryNotes: boolean = false
  ): Promise<Blob | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/bill/${billId}/pdf`,
        {
          params: { addDeliveryNotes },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyCustomers(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminCustomerFilters = {}
  ): Promise<Page<Customer> | ServiceError> {
    try {
      const params: Record<string, string | number> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.email) {
        params.email = filters.email
      }

      if (filters.vatNumber) {
        params.vatNumber = filters.vatNumber
      }

      if (filters.phone) {
        params.phone = filters.phone
      }

      const networkResponse = await this.axiosInstance.get<Page<Customer>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/customer`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyCustomerById(
    companyId: string,
    customerId: string
  ): Promise<Customer | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Customer>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/customer/${customerId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyVehicles(
    companyId: string,
    page: number = 0,
    size: number = 20
  ): Promise<Page<Vehicle> | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Page<Vehicle>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/vehicle`,
        { params: { pageNumber: page, pageSize: size } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyVehicleById(
    companyId: string,
    vehicleId: string
  ): Promise<Vehicle | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Vehicle>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/vehicle/${vehicleId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPlaces(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminPlaceFilters = {}
  ): Promise<Page<Place> | ServiceError> {
    try {
      const params: Record<string, string | number | string[]> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.types?.length) {
        params.types = filters.types
      }

      const networkResponse = await this.axiosInstance.get<Page<Place>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/place`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPlaceById(companyId: string, placeId: string): Promise<Place | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Place>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/place/${placeId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPrices(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminPriceFilters = {}
  ): Promise<Page<Price> | ServiceError> {
    try {
      const params: Record<string, string | number> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.payload) {
        params.payload = filters.payload
      }

      const networkResponse = await this.axiosInstance.get<Page<Price>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/price`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPriceById(companyId: string, priceId: string): Promise<Price | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Price>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/price/${priceId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPayloads(
    companyId: string,
    page: number = 0,
    size: number = 20
  ): Promise<Page<Payload> | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Page<Payload>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/payload`,
        { params: { pageNumber: page, pageSize: size } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyPayloadById(
    companyId: string,
    payloadId: string
  ): Promise<Payload | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Payload>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/payload/${payloadId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyAiRequests(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminAiRequestFilters = {}
  ): Promise<Page<AIRequestListItem> | ServiceError> {
    try {
      const params: Record<string, string | number | boolean> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) params.sort = filters.sort
      if (filters.direction) params.direction = filters.direction
      if (filters.type) params.type = filters.type
      if (filters.startDate) params.startDate = filters.startDate
      if (filters.endDate) params.endDate = filters.endDate
      if (filters.hasError !== undefined) params.hasError = filters.hasError

      const networkResponse = await this.axiosInstance.get<Page<AIRequestListItem>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/ai-request`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyAiRequestById(
    companyId: string,
    aiRequestId: string
  ): Promise<AIRequest | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AIRequest>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/ai-request/${aiRequestId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyDrivers(
    companyId: string,
    page: number = 0,
    size: number = 20,
    filters: AdminDriverFilters = {}
  ): Promise<Page<Driver> | ServiceError> {
    try {
      const params: Record<string, string | number> = {
        pageNumber: page,
        pageSize: size
      }

      if (filters.sort) {
        params.sort = filters.sort
      }

      if (filters.direction) {
        params.direction = filters.direction
      }

      if (filters.name) {
        params.name = filters.name
      }

      if (filters.surname) {
        params.surname = filters.surname
      }

      if (filters.email) {
        params.email = filters.email
      }

      if (filters.nif) {
        params.nif = filters.nif
      }

      if (filters.phoneNumber) {
        params.phoneNumber = filters.phoneNumber
      }

      const networkResponse = await this.axiosInstance.get<Page<Driver>>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/driver`,
        { params }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getCompanyDriverById(
    companyId: string,
    driverId: string
  ): Promise<Driver | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<Driver>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/driver/${driverId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getAllCompanyCustomers(companyId: string): Promise<Customer[] | ServiceError> {
    return this.fetchAllPages((page, size) =>
      this.getCompanyCustomers(companyId, page, size, { sort: 'name', direction: 'asc' })
    )
  }

  async getAllCompanyPlaces(companyId: string): Promise<Place[] | ServiceError> {
    return this.fetchAllPages((page, size) =>
      this.getCompanyPlaces(companyId, page, size, { sort: 'name', direction: 'asc' })
    )
  }

  async getAllCompanyVehicles(companyId: string): Promise<Vehicle[] | ServiceError> {
    return this.fetchAllPages((page, size) => this.getCompanyVehicles(companyId, page, size))
  }

  async getAllCompanyEmployees(companyId: string): Promise<Employee[] | ServiceError> {
    return this.fetchAllPages((page, size) =>
      this.getCompanyEmployees(companyId, page, size, { sort: 'name', direction: 'asc' })
    )
  }

  async getAllCompanyDrivers(companyId: string): Promise<Driver[] | ServiceError> {
    return this.fetchAllPages((page, size) =>
      this.getCompanyDrivers(companyId, page, size, { sort: 'name', direction: 'asc' })
    )
  }

  async getAllCompanyPayloads(companyId: string): Promise<Payload[] | ServiceError> {
    return this.fetchAllPages((page, size) => this.getCompanyPayloads(companyId, page, size))
  }

  async getDashboard(
    startDate: string,
    endDate: string,
    granularity: ChartGranularity
  ): Promise<AdminDashboardData | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AdminDashboardData>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/dashboard`,
        {
          params: {
            startDate,
            endDate,
            granularity
          }
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getBillingSummary(): Promise<AdminBillingSummaryData | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AdminBillingSummaryData>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/billing/summary`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getBillingOrigins(): Promise<BillingOrigin[] | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<BillingOrigin[]>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/billing/origins`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getBillingTrends(
    startDate: string,
    endDate: string,
    granularity: ChartGranularity
  ): Promise<AdminBillingTrendsData | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AdminBillingTrendsData>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/billing/metrics/trends`,
        {
          params: {
            startDate,
            endDate,
            granularity
          }
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getBillingPeriodMetrics(
    startDate: string,
    endDate: string
  ): Promise<AdminBillingPeriodMetricsData | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AdminBillingPeriodMetricsData>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/billing/metrics/period`,
        {
          params: {
            startDate,
            endDate
          }
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getBillingTrials(): Promise<AdminBillingTrialsData | ServiceError> {
    try {
      const networkResponse = await this.axiosInstance.get<AdminBillingTrialsData>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/billing/metrics/trials`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  private async fetchAllPages<T>(
    fetchPage: (page: number, size: number) => Promise<Page<T> | ServiceError>
  ): Promise<T[] | ServiceError> {
    const allItems: T[] = []
    let page = 0

    while (true) {
      const response = await fetchPage(page, FILTER_OPTIONS_PAGE_SIZE)
      if (response instanceof ServiceError) {
        return response
      }

      allItems.push(...response.content)

      if (response.last || response.content.length === 0) {
        break
      }

      page += 1
    }

    return allItems
  }

  async importCustomerCsv(
    companyId: string,
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    skipValidations: boolean,
    defaultTypes: CustomerType[] = []
  ): Promise<CustomerBulkCreateRs | ServiceError> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append(
        'columnMapping',
        JSON.stringify({
          columns: columnMapping,
          delimiter,
          ...(defaultTypes.length > 0 ? { defaultTypes } : {})
        })
      )
      formData.append('skipValidations', String(skipValidations))

      const networkResponse = await this.axiosInstance.post<CustomerBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/customer/imports/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importEmployeeCsv(
    companyId: string,
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    skipValidations: boolean,
    skipCreateUser: boolean,
    defaultRole?: Role
  ): Promise<EmployeeBulkCreateRs | ServiceError> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append(
        'columnMapping',
        JSON.stringify({
          columns: columnMapping,
          delimiter,
          ...(defaultRole !== undefined ? { defaultRole } : {}),
        })
      )
      formData.append('skipValidations', String(skipValidations))
      formData.append('skipCreateUser', String(skipCreateUser))

      const networkResponse = await this.axiosInstance.post<EmployeeBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/employee/imports/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importVehicleCsv(
    companyId: string,
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    skipValidations: boolean,
    skipNormalization: boolean
  ): Promise<VehicleBulkCreateRs | ServiceError> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('columnMapping', JSON.stringify({ columns: columnMapping, delimiter }))
      formData.append('skipValidations', String(skipValidations))
      formData.append('skipNormalization', String(skipNormalization))

      const networkResponse = await this.axiosInstance.post<VehicleBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/vehicle/imports/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importPlaceCsv(
    companyId: string,
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    skipValidations: boolean,
    defaultTypes: PlaceType[] = []
  ): Promise<PlaceBulkCreateRs | ServiceError> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append(
        'columnMapping',
        JSON.stringify({
          columns: columnMapping,
          delimiter,
          ...(defaultTypes.length > 0 ? { defaultTypes } : {})
        })
      )
      formData.append('skipValidations', String(skipValidations))

      const networkResponse = await this.axiosInstance.post<PlaceBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/place/imports/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async importPayloadCsv(
    companyId: string,
    file: File,
    columnMapping: { columnIndex: number; field: string }[],
    delimiter: string,
    skipValidations: boolean
  ): Promise<PayloadBulkCreateRs | ServiceError> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('columnMapping', JSON.stringify({ columns: columnMapping, delimiter }))
      formData.append('skipValidations', String(skipValidations))

      const networkResponse = await this.axiosInstance.post<PayloadBulkCreateRs>(
        `${import.meta.env.VITE_API_BASE_URL}/admin/company/${companyId}/payload/imports/csv`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const adminService = new AdminService()
